import React, { useEffect, useState } from "react";
import Loader from "../../Loader/Loader";
import { useEvent } from "../../../context/EventContext";
import eventsAPI from "../../../api/eventsAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// The Registration component renders the form for adding a new registration type
const Registration = ({ onNext }) => {
  const {
    loading,
    types,
    setTypes,
    mainEventID,
    setTypeEditIndex,
    setRegistraionFormData,
    handleRegisterChange,
    registrationFormData,
    setLoading,
    typeEditIndex,
    TypeAdded,
    setTypeAdded,
    editIndex,
    formData,
    registrationData,
  } = useEvent();

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isRegistrationStarted, setIsRegistrationStarted] = useState(false);

  // filter the  use registration data
  const [filterRegistrationType, setFilterRegistrationType] = useState(""); // For filtering by registration type
  const [sortJobStatus, setSortJobStatus] = useState(""); // For sorting by job status
  const [filteredData, setFilteredData] = useState([]); // For holding the final filtered and sorted data

  // Filter and sort the data whenever filter or sort criteria changes
  useEffect(() => {
    let updatedData = registrationData;
    // Apply filter by registration type if set
    if (filterRegistrationType) {
      updatedData = updatedData.filter(
        (registration) =>
          registration.registrationTypeID?._id === filterRegistrationType
      );
    }
    // Apply filter by job status if set
    if (sortJobStatus) {
      updatedData = updatedData.filter(
        (registration) => registration.userID?.jobStatus === sortJobStatus
      );
    }
    setFilteredData(updatedData);
  }, [filterRegistrationType, sortJobStatus, registrationData]);

  // Clear all filters and sorting
  const clearFiltersAndSorting = () => {
    setFilterRegistrationType("");
    setSortJobStatus("");
    setFilteredData(registrationData); // Reset to show all data
  };

  // Check if the registration start date has passed
  useEffect(() => {
    if (formData?.registration_start_date) {
      const currentDate = new Date();
      const registrationStartDate = new Date(formData?.registration_start_date);

      // Reset the time to ensure the comparison is only based on the date (ignoring hours)
      currentDate.setHours(0, 0, 0, 0);
      registrationStartDate.setHours(0, 0, 0, 0);

      // Disable inputs if the current date is greater than the registration start date
      if (currentDate >= registrationStartDate) {
        setIsRegistrationStarted(true); // Disable the inputs
      } else {
        setIsRegistrationStarted(false); // Enable the inputs
      }
    }
  }, [formData]);

//   console.log(isRegistrationStarted);

  // Handle Add Button
  const handleAddType = () => {
    setRegistraionFormData({
      typeName: "",
      price: "",
      description: "",
      mainEventID: mainEventID,
    });
    setIsFormVisible(true);
    setTypeEditIndex(null);
  };

  // Handle Edit Button
  const handleEdit = (index) => {
    setRegistraionFormData(types[index]);
    setIsFormVisible(true);
    setTypeEditIndex(index);
  };

  //   * Handles the "Save" button click in the registration form.
  //   * If the request is successful, it shows a success message, sets the isFormVisible state to false, and sets the TypeAdded state to true.
  //   * It also updates the types state with the newly created registration type.
  //   * If the typeEditIndex state is not null, it updates the registration type at that index in the types state.
  //   * Otherwise, it adds the new registration type to the types state.
  //   * If the request fails, it shows an error message.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/registration",
        registrationFormData
      );
      if (response.status === 200) {
        toast.success("Registration added successfully!");
        setLoading(false);
        setIsFormVisible(false);
        setTypeAdded(true);

        if (typeEditIndex !== null) {
          // Update the type that was edited
          const updatedRegistrations = types.map((type, index) =>
            index === typeEditIndex ? response.data : type
          );
          setTypes(updatedRegistrations);
        } else {
          // Add the new type if no edit index is present
          setTypes((prevTypes) => [...prevTypes, response.data]);
        }
      }
    } catch (error) {
      console.log("Error creating Registration:", error);
      setLoading(false);
    }
  };

  //   * Handles the "Update" button click in the registration form.
  //   * If the request is successful, it shows a success message, sets the isFormVisible state to false, and updates the types state with the updated registration data.
  //   * If the request fails, it shows an error message.
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/updateRegistration",
        registrationFormData
      );

      if (response.status === 200) {
        toast.success("Registration data updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update the local state with the updated registration data
        const updatedRegistration = types.map((type, index) =>
          index === editIndex ? response.data : type
        );
        setTypes(updatedRegistration);
      }
    } catch (error) {
      console.log("Error updating Registration:", error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a registration type by sending a DELETE request to the
  //   * /organizer/registrationType/{id} endpoint.
  //   * If successful, it removes the registration type from the local state and shows a
  //   * success message.
  //   * If the request fails, it shows an error message.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(
        `/organizer/registrationType/${id}`
      ); // Pass the location's id

      if (response.status === 200) {
        toast.success("Registration Type deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Remove the deleted location from the local state
        const updatedTypes = types.filter((type) => type._id !== id);
        setTypes(updatedTypes);
      }
    } catch (error) {
      console.log("Error deleting Registration Type:", error);
      setLoading(false);
    }
  };

  return (
    <div className="registration-section">
      {/* Show the "Add Registration Types" button when no types are added and the form is not visible */}
      {!isFormVisible && types.length === 0 && !TypeAdded && (
        <button className="add-space" onClick={handleAddType}>
          Add Registration Types
        </button>
      )}

      {!isFormVisible && TypeAdded && (
        <>
          {types.map((type, index) => (
            <div className="saved-details" key={index}>
              <p>
                <span>Type: </span> {type.typeName}
              </p>
              <p>
                <span>Ticket Price:</span> {type.price}
              </p>
              <button className="add-space" onClick={() => handleEdit(index)}>
                Edit
              </button>
            </div>
          ))}

          <div className="buttons-controller">
            <button className="add-space" onClick={handleAddType}>
              Add
            </button>
            <button className="add-space" onClick={onNext}>
              Next
            </button>
          </div>

          <div className="user-registration-data">
            {/* Registered Users Table (New Table) */}
            {registrationData.length > 0 && (
              <>
                <div className="sorting-filter">
                  <p>Filter By Registration Type</p>
                  <select
                    value={filterRegistrationType}
                    onChange={(e) => setFilterRegistrationType(e.target.value)}
                  >
                    <option value="">select</option>
                    {types.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.typeName}
                      </option>
                    ))}
                  </select>
                  <select
                    value={sortJobStatus}
                    onChange={(e) => setSortJobStatus(e.target.value)}
                  >
                    <option>Sort By Job Status</option>
                    <option value="student">Student</option>
                    <option value="employed">Employed</option>
                    <option value="unemployed">Unemployed</option>
                    <option value="self-employed">Self-Employed</option>
                  </select>
                  <button
                    className="add-space"
                    onClick={() => clearFiltersAndSorting()}
                  >
                    Clear
                  </button>
                </div>
                <div className="user-registration-table">
                  <h3>Registered Users</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Profile Picture</th>
                        <th>Registration Type</th>
                        <th>Job Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.length > 0 ? (
                        filteredData.map((registration, index) => (
                          <tr key={index}>
                            <td>{registration.userID?.firstName}</td>
                            <td>{registration.userID?.email}</td>
                            <td>
                              <img
                                src={registration.userID?.profilePicture}
                                alt="Profile Pic"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "50%",
                                }}
                              />
                            </td>
                            <td>{registration.registrationTypeID?.typeName}</td>
                            <td>{registration.userID?.jobStatus}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5">
                            <p>No data available</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {isFormVisible && (
        <form>
          <h2>Registration Type</h2>
          <div className="space-details">
            <label htmlFor="space-name">Type of attendee</label>
            <input
              name="typeName"
              type="text"
              placeholder="Enter the Type of attendee"
              value={registrationFormData.typeName}
              onChange={handleRegisterChange}
              disabled={typeEditIndex !== null && isRegistrationStarted} // Disable only in edit mode and if registration has started
            />
            <label htmlFor="space-code">Ticket Price</label>
            <input
              name="price"
              type="number"
              placeholder="Enter the Price of the Ticket"
              value={registrationFormData.price}
              onChange={handleRegisterChange}
              disabled={typeEditIndex !== null && isRegistrationStarted} // Disable only in edit mode and if registration has started
            />
            <label htmlFor="space-type">Description</label>
            <input
              name="description"
              type="text"
              placeholder="Type Description for Attendee Types"
              value={registrationFormData.description}
              onChange={handleRegisterChange}
              disabled={typeEditIndex !== null && isRegistrationStarted} // Disable only in edit mode and if registration has started
            />
          </div>

          {/* Show the "Save" button if adding a new type, and "Update" button if editing */}
          {typeEditIndex === null ? (
            <button
              type="submit"
              className="create-button"
              onClick={handleSave}
            >
              {loading ? <Loader /> : "SAVE"}
            </button>
          ) : (
            <>
              <button
                type="submit"
                className="create-button"
                onClick={handleUpdate}
              >
                {loading ? <Loader /> : "UPDATE"}
              </button>
              <button
                type="submit"
                className="create-button"
                disabled={typeEditIndex !== null && isRegistrationStarted} // Disable only in edit mode and if registration has started
                onClick={(e) => handleDelete(e, registrationFormData._id)}
                style={{
                    cursor: isRegistrationStarted ? 'not-allowed' : 'pointer'
                  }}
              >
                {loading ? <Loader /> : "DELETE"}
              </button>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default Registration;
